import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppRequestSchema } from '@codebox/shared';
import { api, API_BASE_URL, hasSession } from '../lib/api';
import { CodeBoxMark } from '../components/BrandLogo';

interface PlayApp {
  projectId: string;
  title: string;
  description: string | null;
  projectType: 'game' | 'app' | 'tool';
  thumbnailUrl: string | null;
  creator: string;
  visibility: 'private' | 'public';
  releaseName: string | null;
  publishedAt: string;
}

/** Same per-minute client-side ceilings as the studio preview broker. */
const CLIENT_LIMITS: Record<string, number> = { 'ai.text': 12, 'ai.image': 4, 'data.op': 60, 'auth.op': 30, 'assets.get': 30, export: 10, 'realtime.op': 40 };

/**
 * The public play page — a CLIENT page on the studio origin, per the architecture rule that pages
 * live on codebox.techedify.com and APIs under prod-api…/box. Isolation is layer-1 of the studio
 * preview, reused verbatim: the app runs in an opaque-origin `sandbox="allow-scripts"` iframe and
 * can never touch studio storage or register a service worker. This page is layer 3 — it brokers
 * the app's `CodeBox.*` postMessage RPCs to the release's public bridge (`/box/play/:slug/bridge`)
 * and its multiplayer frames to `/box/ws`, joining rooms by SLUG (guests allowed, #29/#30).
 */
export function PlayPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [app, setApp] = useState<PlayApp | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [remixing, setRemixing] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const rateRef = useRef<Map<string, { until: number; count: number }>>(new Map());
  const wsRef = useRef<WebSocket | null>(null);
  const joinWaiters = useRef<Array<(info: { code: string }) => void>>([]);

  useEffect(() => {
    setApp(null);
    setHtml(null);
    setFailed(false);
    void (async () => {
      try {
        const data = await api.get<{ app: PlayApp; bundle: { html: string } }>(`/box/play/${slug}`);
        setApp(data.app);
        setHtml(data.bundle.html);
        document.title = `${data.app.title} — Code Box`;
      } catch {
        setFailed(true);
      }
    })();
    return () => {
      document.title = 'Code Box';
    };
  }, [slug]);

  const overLimit = (type: string): boolean => {
    const limit = CLIENT_LIMITS[type] ?? 30;
    const now = Date.now();
    const entry = rateRef.current.get(type);
    if (!entry || entry.until < now) {
      rateRef.current.set(type, { until: now + 60_000, count: 1 });
      return false;
    }
    entry.count += 1;
    return entry.count > limit;
  };

  const reply = useCallback((id: string, ok: boolean, result?: unknown, message?: string) => {
    frameRef.current?.contentWindow?.postMessage(
      { v: 'cbx:v1', type: 'response', id, ok, result, error: ok ? undefined : { code: 'bridge', message: message ?? 'That did not work' } },
      '*',
    );
  }, []);

  const pushToApp = useCallback((payload: unknown) => {
    frameRef.current?.contentWindow?.postMessage({ v: 'cbx:v1', type: 'realtime.push', id: 'push', payload }, '*');
  }, []);

  /* ------------------------- multiplayer: a per-page guest socket ------------------------- */
  // The studio's shared transport is project- and auth-oriented; a play session joins by slug as a
  // guest, so it gets its own socket with the page's lifetime.
  const socket = useCallback((): Promise<WebSocket> => {
    const existing = wsRef.current;
    if (existing && existing.readyState <= WebSocket.OPEN) return Promise.resolve(existing);
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(`${API_BASE_URL.replace(/^http/, 'ws')}/box/ws`);
      ws.onopen = () => {
        wsRef.current = ws;
        resolve(ws);
      };
      ws.onerror = () => reject(new Error('multiplayer unavailable'));
      ws.onmessage = event => {
        try {
          const frame = JSON.parse(String(event.data)) as { t?: string; code?: unknown };
          if (frame.t === 'app-joined') {
            joinWaiters.current.shift()?.({ code: String(frame.code) });
            pushToApp(frame);
          } else if (frame.t === 'app-message' || frame.t === 'app-state' || frame.t === 'app-presence' || frame.t === 'app-error') {
            pushToApp(frame);
          }
        } catch {
          /* not a frame */
        }
      };
    });
  }, [pushToApp]);

  useEffect(() => {
    return () => {
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [slug]);

  /* --------------------------------- the bridge broker --------------------------------- */
  useEffect(() => {
    if (!slug) return;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) return;

      // room.handler calls ride outside the typed schema (same shape PreviewPane relays raw)
      const raw = event.data as { v?: string; type?: string; id?: string; payload?: { op?: string; message?: { name?: string; args?: unknown } } };
      if (raw?.v === 'cbx:v1' && raw.type === 'realtime.op' && raw.payload?.op === 'handler' && raw.id) {
        wsRef.current?.send(JSON.stringify({ t: 'app-handler', name: raw.payload.message?.name, args: raw.payload.message?.args }));
        reply(raw.id, true, { called: true });
        return;
      }

      const parsed = AppRequestSchema.safeParse(event.data);
      if (!parsed.success) return;
      const message = parsed.data;

      switch (message.type) {
        case 'bridges-ready':
        case 'runtime-error':
        case 'navigate-attempt':
          return; // a viewer can't fix the app — errors stay quiet here
        case 'realtime.op': {
          if (overLimit('realtime.op')) return reply(message.id, false, undefined, 'Take a short break');
          const op = message.payload.op;
          if (op === 'join') {
            void socket()
              .then(ws => {
                joinWaiters.current.push(info => reply(message.id, true, info));
                const code = message.payload.room === 'new' ? undefined : message.payload.room;
                ws.send(JSON.stringify({ t: 'app-join', slug, code }));
              })
              .catch(() => reply(message.id, false, undefined, 'Multiplayer is unavailable right now'));
          } else if (op === 'send') {
            wsRef.current?.send(JSON.stringify({ t: 'app-send', message: message.payload.message }));
            reply(message.id, true, { sent: true });
          } else if (op === 'presence') {
            reply(message.id, true, []); // presence rides the push frames
          } else if (op === 'leave') {
            wsRef.current?.send(JSON.stringify({ t: 'app-leave' }));
            reply(message.id, true, { left: true });
          }
          return;
        }
        default: {
          if (overLimit(message.type)) return reply(message.id, false, undefined, 'Take a short break — too many requests');
          void api
            .post<{ ok: boolean; result?: unknown }>(`/box/play/${slug}/bridge`, {
              type: message.type,
              payload: message.payload,
            })
            .then(body => reply(message.id, true, body.result))
            .catch(err => reply(message.id, false, undefined, err instanceof Error ? err.message : undefined));
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [slug, reply, socket]);

  const remix = async () => {
    if (!app) return;
    if (!hasSession()) {
      navigate('/sign-in');
      return;
    }
    setRemixing(true);
    try {
      const { project } = await api.post<{ project: { id: string } }>(`/box/projects/${app.projectId}/remix`);
      navigate(`/projects/${project.id}`);
    } catch {
      setRemixing(false);
    }
  };

  if (failed) {
    return (
      <div className="grid min-h-screen place-items-center bg-page p-6">
        <div className="text-center">
          <CodeBoxMark className="mx-auto h-10 w-10 opacity-60" />
          <h1 className="mt-4 text-lg font-bold">This app is not available</h1>
          <p className="mt-1 text-sm text-muted">It may have been unpublished, or the link is wrong.</p>
          <Link
            to="/playstore"
            className="mt-5 inline-flex h-9 items-center rounded-xl bg-accent px-4 text-sm font-bold text-accent-ink transition hover:brightness-110"
          >
            Explore the playstore
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-page">
      {/* slim frosted bar in the studio toolbar's language; the app below is the subject */}
      <header className="flex h-12 shrink-0 items-center gap-3 bg-surface/80 px-3 backdrop-blur-md">
        <Link to="/playstore" aria-label="Code Box playstore" className="shrink-0">
          <CodeBoxMark className="h-7 w-7" />
        </Link>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-extrabold leading-tight">{app?.title ?? 'Loading…'}</p>
          {app && <p className="truncate text-[11px] leading-tight text-muted">by {app.creator}</p>}
        </div>
        {app?.visibility === 'private' && (
          <span className="hidden rounded-full bg-page px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-muted sm:inline">
            Link only
          </span>
        )}
        <button
          type="button"
          onClick={() => void remix()}
          disabled={!app || remixing || app.visibility !== 'public'}
          title={app?.visibility === 'public' ? 'Copy this app into your own workspace' : 'Only playstore apps can be remixed'}
          className="inline-flex h-8 items-center rounded-lg bg-accent px-3 text-xs font-bold text-accent-ink transition hover:brightness-110 disabled:opacity-40"
        >
          {remixing ? 'Copying…' : 'Remix'}
        </button>
      </header>

      <main className="min-h-0 flex-1">
        {html === null ? (
          <div className="grid h-full place-items-center">
            {app?.thumbnailUrl ? (
              <img src={app.thumbnailUrl} alt="" className="h-40 w-64 animate-pulse rounded-2xl object-cover opacity-70" />
            ) : (
              <div className="h-40 w-64 animate-pulse rounded-2xl bg-surface" />
            )}
          </div>
        ) : (
          <iframe
            ref={frameRef}
            title={app?.title ?? 'Published app'}
            // opaque origin — allow-same-origin must never be added here (same rule as PreviewPane)
            sandbox="allow-scripts"
            srcDoc={html}
            className="h-full w-full border-0 bg-white"
          />
        )}
      </main>
    </div>
  );
}
